"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PageSeo = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const theme_kit_core_1 = require("@prezly/theme-kit-core");
const router_1 = require("next/router");
const next_seo_1 = require("next-seo");
const react_1 = require("react");
const newsroom_context_1 = require("../../newsroom-context");
const lib_1 = require("./lib");
/**
 * This component provides base page attributes for every Newsroom page.
 * It depends on information from NewsroomContext, so the component should be placed inside the `NewsroomContextProvider`.
 * You can pass additional props expected by NextSeo component to override any info that is provided by the NewsroomContext.
 */
function PageSeo({ title, description, imageUrl, canonical, openGraph, twitter, ...nextSeoProps }) {
    const companyInformation = (0, newsroom_context_1.useCompanyInformation)();
    const site = (0, newsroom_context_1.useNewsroom)();
    const currentLocale = (0, newsroom_context_1.useCurrentLocale)();
    const languages = (0, newsroom_context_1.useLanguages)();
    const getTranslationUrl = (0, newsroom_context_1.useGetTranslationUrl)();
    const getLinkLocaleSlug = (0, newsroom_context_1.useGetLinkLocaleSlug)();
    const currentStory = (0, newsroom_context_1.useCurrentStory)();
    const { asPath, basePath } = (0, router_1.useRouter)();
    const currentPath = `${basePath}${asPath}`;
    const pageTitle = (0, react_1.useMemo)(() => {
        const defaultMetaTitle = companyInformation.seo_settings.meta_title ||
            companyInformation.seo_settings.default_meta_title ||
            companyInformation.name;
        if (title && typeof title === 'function') {
            return title(defaultMetaTitle);
        }
        return title || defaultMetaTitle;
    }, [title, companyInformation]);
    const pageDescription = description ||
        companyInformation.seo_settings.meta_description ||
        companyInformation.seo_settings.default_meta_description ||
        companyInformation.about_plaintext;
    const canonicalUrl = canonical || (0, lib_1.getAbsoluteUrl)(currentPath, site.url, getLinkLocaleSlug(currentLocale));
    const siteName = companyInformation.name;
    const sharingImageUrl = imageUrl || (0, theme_kit_core_1.getNewsroomOgImageUrl)(site, currentLocale);
    const alternateLanguageLinks = (0, react_1.useMemo)(() => {
        if (!languages.length) {
            return [];
        }
        const usedLanguages = (0, theme_kit_core_1.getUsedLanguages)(languages);
        return (0, theme_kit_core_1.getAlternateLanguageLinks)(usedLanguages, (locale) => {
            const translationUrl = getTranslationUrl(locale);
            if (!translationUrl) {
                return undefined;
            }
            return (0, lib_1.getAbsoluteUrl)(translationUrl, site.url, currentStory && translationUrl !== '/' ? false : getLinkLocaleSlug(locale));
        });
    }, [currentStory, getLinkLocaleSlug, getTranslationUrl, languages, site.url]);
    return ((0, jsx_runtime_1.jsx)(next_seo_1.NextSeo, { title: pageTitle, description: pageDescription, canonical: canonicalUrl, noindex: nextSeoProps.noindex || !site.is_indexable, nofollow: nextSeoProps.nofollow || !site.is_indexable, openGraph: {
            url: canonicalUrl,
            title: pageTitle,
            description: pageDescription,
            locale: currentLocale.toUnderscoreCode(),
            images: [
                {
                    url: sharingImageUrl,
                    alt: pageTitle,
                },
            ],
            site_name: siteName,
            ...openGraph,
        }, twitter: {
            site: siteName,
            cardType: 'summary',
            ...twitter,
        }, additionalMetaTags: [
            { name: 'twitter:image', content: sharingImageUrl },
            site.google_search_console_key && {
                name: 'google-site-verification',
                content: site.google_search_console_key,
            },
        ].filter((tag) => Boolean(tag)), additionalLinkTags: [
            {
                rel: 'alternate',
                type: 'application/rss+xml',
                href: (0, lib_1.getAbsoluteUrl)(`${basePath}/feed`, site.url),
            },
        ], languageAlternates: alternateLanguageLinks, ...nextSeoProps }));
}
exports.PageSeo = PageSeo;
//# sourceMappingURL=PageSeo.js.map